import { Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  pickAuthoredText,
  plannedTargetLabel,
  programRoundLabel,
  type Locale,
  type PlannedSet,
  type Program,
  type ProgramSimulation,
  type UnitsPreference,
} from "@heraion/shared";

import { amrapStopSentence } from "../amrapStopWords";
import { Eyebrow } from "../primitives/Eyebrow";
import type { UiTheme } from "../theme";
import { weightUnitLabels } from "../weightUnitLabels";
import { Pair } from "./simulationShared";
import type { SimulationStyles } from "./simulationStyles";

const minutes = (sec: number) => Math.max(1, Math.round(sec / 60));

// #355: the simulator, day by day. The rows that never progress, how long
// each day lasts, and every AMRAP set with its load and its stop rule.
export function SimulationDays({
  theme,
  styles,
  sim,
  program,
  resolveName,
  unit,
  lang,
}: {
  theme: UiTheme;
  styles: SimulationStyles;
  sim: ProgramSimulation;
  program: Program;
  resolveName: (slug: string) => string;
  unit: UnitsPreference;
  lang: Locale;
}) {
  const { t } = useTranslation();
  const day = (i: number) =>
    pickAuthoredText(program.sessions[i]?.name, lang, program.authorLocale) ??
    t("mobile.plan.sessionN", { n: i + 1, defaultValue: "Session {{n}}" });
  const untimed = Math.max(0, ...sim.days.flatMap((d) => d.weeks.map((w) => w.untimedSets)));
  const labels = weightUnitLabels(t);
  // A row loaded by effort is one line: its weight can still rise. Only the
  // fixed ones (kg or a %) are listed one by one.
  const fixedRows = sim.staticRows.filter((r) => !r.loadByEffort);
  const effortRows = sim.staticRows.length - fixedRows.length;
  return (
    <>
      <View style={styles.block}>
        <Eyebrow theme={theme}>
          {t("simulator.staticTitle", { defaultValue: "Rows that stay the same in every round" })}
        </Eyebrow>
        {sim.staticRows.length === 0 ? (
          <Text style={styles.quiet}>
            {t("simulator.staticNone", { defaultValue: "None: every row changes over the cycle." })}
          </Text>
        ) : (
          <>
            {fixedRows.map((r) => (
              <Text key={`${r.sessionIdx}-${r.slotId}`} style={styles.line}>
                {t("simulator.staticRow", {
                  day: day(r.sessionIdx),
                  exercise: resolveName(r.exerciseId),
                  count: r.rounds,
                  defaultValue: "{{day}} · {{exercise}}: the same in {{count}} rounds",
                })}
              </Text>
            ))}
            {fixedRows.length === 0 ? (
              <Text style={styles.quiet}>{t("simulator.staticNoFixed", { defaultValue: "None has a fixed weight." })}</Text>
            ) : null}
            {effortRows > 0 ? (
              <Text style={styles.quiet}>
                {t("simulator.staticByEffortRows", {
                  count: effortRows,
                  defaultValue: "{{count}} rows load by effort: the weight can still rise",
                })}
              </Text>
            ) : null}
          </>
        )}
      </View>
      {sim.days.length > 0 ? (
        <View style={styles.block}>
          <Eyebrow theme={theme}>{t("simulator.durationTitle", { defaultValue: "Session length, estimated" })}</Eyebrow>
          {sim.days.map((d) => {
            const lo = minutes(d.minSeconds);
            const hi = minutes(d.maxSeconds);
            return (
              <Pair
                key={d.sessionId || d.sessionIdx}
                styles={styles}
                label={day(d.sessionIdx)}
                value={
                  lo === hi
                    ? t("simulator.minutes", { n: lo, defaultValue: "{{n}} min" })
                    : t("simulator.minutesRange", { min: lo, max: hi, defaultValue: "{{min}} to {{max}} min" })
                }
              />
            );
          })}
          {untimed > 0 ? (
            <Text style={styles.quiet}>
              {t("simulator.untimed", {
                count: untimed,
                defaultValue: "{{count}} sets with no time: their rest counts, their work does not",
              })}
            </Text>
          ) : null}
        </View>
      ) : null}
      {sim.amrap.length > 0 ? (
        <View style={styles.block}>
          <Eyebrow theme={theme}>{t("simulator.amrapTitle", { defaultValue: "AMRAP sets" })}</Eyebrow>
          {sim.amrap.map((a) => {
            const set = {
              targetReps: a.floorReps,
              targetWeight: a.load,
              ...(a.targetRir !== null ? { targetRir: a.targetRir } : {}),
            } as PlannedSet;
            const load = plannedTargetLabel(set, unit, lang, labels);
            return (
              <Text key={`${a.weekIdx}-${a.sessionIdx}-${a.exerciseId}-${a.setIdx}`} style={styles.line}>
                {t("simulator.amrapRow", {
                  week: t("mobile.plan.programWeekShort", {
                    n: a.weekIdx + 1,
                    defaultValue: programRoundLabel(a.weekIdx + 1),
                  }),
                  day: day(a.sessionIdx),
                  exercise: resolveName(a.exerciseId),
                  set: a.setIdx + 1,
                  reps: a.floorReps,
                  defaultValue: "{{week}} · {{day}} · {{exercise}}, set {{set}}: at least {{reps}}",
                })}
                {load ? ` · ${load}` : ""}
                {" · "}
                {a.stop
                  ? amrapStopSentence(t, a.stop)
                  : t("simulator.amrapNoStop", { defaultValue: "no stop rule written" })}
              </Text>
            );
          })}
        </View>
      ) : null}
    </>
  );
}
